import { AuthService } from '@/service/auth/auth';
import { getAccessToken } from '@/utils/auth';
import {
  ACCESS_TOKEN,
  DEMO_BLOCKED_EVENT,
  DEMO_MODE,
  REFRESH_TOKEN,
} from '@/utils/utils';
import axios, { InternalAxiosRequestConfig, isAxiosError } from 'axios';

export const routes = {
  auth: {
    signin: '/auth/sign-in',
    signinCode: '/auth/sign-in/code',
    signinFirebase: '/auth/sign-in/firebase',
    signup: '/auth/sign-up',
    sendCode: '/auth/send-code',
    token: '/auth/token',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    demo: '/auth/demo',
  },
  bankInfo: {
    get: '/bank-info',
    put: '/bank-info',
  },
  user: {
    getMe: '/user',
    getSubscription: '/user/subscription',
    trial: '/user/trial',
    update: '/user',
    avatar: '/user/avatar',
    banner: '/user/banner',
    updatePassword: '/user/update-password',
    changeEmail: '/user/change-email',
    confirmChangeEmail: '/user/change-email/confirm',
    search: '/user/search',
    completeOnboarding: '/user/onboarding',
    replayOnboarding: '/user/onboarding/replay',
    heardFrom: '/user/heard-from',
    preferences: '/user/preferences',
    sectionTime: '/user/section-time',
    deleteAccount: '/user',
    getPublicUser: (userId: string) => `/user/${userId}`,
    admin: {
      search: '/user/admin/search',
      hideProfile: (userId: string) => `/user/admin/${userId}/hide-profile`,
      lifetime: (userId: string) => `/user/admin/${userId}/lifetime`,
      affiliation: (userId: string) => `/user/admin/${userId}/affiliation`,
      admin: (userId: string) => `/user/admin/${userId}/admin`,
    },
  },
  moderation: {
    reports: '/moderation/reports',
  },
  payment: {
    getInvoices: `/payment/invoices`,
    getSubscriptions: `/payment/subscription/list`,
    createSubscription: `/payment/subscription`,
    stopSubscription: `/payment/subscription`,
    getPaymentMethods: `/payment/payment-methods`,
    deletePaymentMethod: `/payment/payment-method`,
    createSetupIntent: `/payment/setup-intent`,
    setDefaultPaymentMethod: `/payment/default-payment-method`,
    getMyAddress: `/payment/address/my`,
    updateMyAddress: `/payment/address/my`,
    appleStore: `/payment/store/apple`,
    appleTransactions: `/payment/store/apple/transactions`,
    googleStore: `/payment/store/google`,
    googlePurchases: `/payment/store/google/purchases`,
    promoOffer: `/payment/promo-offer`,
    promoOptOut: `/payment/promo-offer/opt-out`,
  },
  trackedAccounts: {
    getAccounts: `/tracked-account`,
    createAccount: `/tracked-account`,
    manualNotices: `/tracked-account/manual-notices`,
    manualNoticeAck: (id: string) => `/tracked-account/${id}/manual-notice-ack`,
    inputErrorNotices: `/tracked-account/input-error-notices`,
    inputErrorNoticeAck: (id: string) =>
      `/tracked-account/${id}/input-error-notice-ack`,
    updateAccount: (id: string) => `/tracked-account/${id}`,
    deleteAccount: (id: string) => `/tracked-account/${id}`,
    deleteCopySettings: (id: string) => `/tracked-account/${id}/settings`,
    getStopChanges: (accountId: string, posId: number) =>
      `/tracked-account/${accountId}/positions/${posId}/stop-changes`,
    getCopyDecisions: (accountId: string, limit: number) =>
      `/tracked-account/${accountId}/copy-decisions?limit=${limit}`,
    getMasterStatus: (accountId: string) =>
      `/tracked-account/${accountId}/master-status`,
    getPositionExcursion: (accountId: string, posId: number) =>
      `/tracked-account/${accountId}/positions/${posId}/excursion`,
    recomputePositionExcursion: (accountId: string, posId: number) =>
      `/tracked-account/${accountId}/positions/${posId}/excursion/recompute`,
    getLatentEquity: '/tracked-account/latent-equity',
    getAccountLatentEquity: (accountId: string) =>
      `/tracked-account/${accountId}/latent-equity`,
    computeExcursions: '/tracked-account/excursions/compute',
    computeAccountExcursions: (accountId: string) =>
      `/tracked-account/${accountId}/excursions/compute`,
    updateCopySettings: `/tracked-account/settings`,
    updateMasterSettings: (id: string) =>
      `/tracked-account/${id}/update-master-account`,
    getLiveData: (id: string) => `/tracked-account/${id}/live-data`,
    getAllLiveData: `/tracked-account/live-data`,
    getUserLiveData: (userId: string) =>
      `/tracked-account/live-data/user/${userId}`,
    getAccountStats: (id: string) => `/tracked-account/${id}/stats`,
    getAllStats: `/tracked-account/stats`,
    getUserStats: (userId: string) => `/tracked-account/stats/${userId}`,
    getBarebonesAccounts: (userId: string) => `/tracked-account/user/${userId}`,
    getSimulatedFixedRisk: '/tracked-account/stats/simulate/fixed',
    getSimulatedMartingale: '/tracked-account/stats/simulate/martingale',
    getSimulatedDrawdown: '/tracked-account/stats/simulate/drawdown',
  },
  mail: {
    reportBug: `/mail/report-bug`,
    contactUs: `/mail/contact-us`,
  },
  affiliates: {
    get: '/affiliate-revenu',
  },
  adminServers: {
    overview: '/admin/servers',
  },
  farm: {
    connection: (accountId: string) => `/farm/connection/${accountId}`,
    adminConnections: '/farm/admin/connections',
    adminSummary: '/farm/admin/summary',
    diagnose: (accountId: string) =>
      `/farm/admin/connections/${accountId}/diagnose`,
  },
  adminMetrics: {
    overview: '/admin/metrics/overview',
    timeline: '/admin/metrics/timeline',
    signups: '/admin/metrics/signups',
    acquisition: '/admin/metrics/acquisition',
    conversion: '/admin/metrics/conversion',
    subscribers: '/admin/metrics/subscribers',
    events: '/admin/metrics/events',
    trackerErrors: '/admin/metrics/tracker-errors',
    sectionTimes: '/admin/metrics/section-times',
  },
  adminAccounts: {
    history: '/admin/accounts/history',
    retryFailed: '/admin/accounts/retry-failed',
    retry: (uid: string) => `/admin/accounts/${uid}/retry`,
    update: (uid: string) => `/admin/accounts/${uid}`,
    remove: (uid: string) => `/admin/accounts/${uid}`,
    failureCategory: (uid: string) => `/admin/accounts/${uid}/failure-category`,
    partnerClaims: '/admin/accounts/partner-claims',
    partnerClaim: (uid: string) => `/admin/accounts/${uid}/partner-claim`,
  },
  announcements: {
    pending: '/announcements/pending',
    ack: (id: string) => `/announcements/${id}/ack`,
    admin: '/announcements/admin',
    adminById: (id: string) => `/announcements/admin/${id}`,
    adminImage: (id: string) => `/announcements/admin/${id}/image`,
  },
  scoring: {
    scoreboard: '/scores',
    compute: '/scores/compute-scores',
    share: '/scores/share',
    publicRank: (slug: string) => `/public/rank/${slug}`,
    rankGuide: '/scores/rank-guide',
    userRank: (userId: string) => `/scores/user/${userId}`,
    allStats: '/scores/all-stats',
  },
  communityStats: {
    get: '/community-stats',
  },
  symbols: {
    getCurrencySymbols: () => `/symbols`,
    getAccountSymbolData: (symbol: string) => `/symbols/${symbol}`,
  },
  positions: {
    takePositions: '/positions',
    orders: '/positions/orders',
    recentOrders: '/positions/orders/recent',
    symbolSpec: '/positions/symbol-spec',
    close: '/positions/close',
    partialClose: '/positions/partial-close',
    closeStatus: '/positions/close-status',
    modifyPosition: '/positions/modify-position',
    cancelOrder: '/positions/cancel-order',
    modifyOrder: '/positions/modify-order',
  },
  riskEngine: {
    settings: '/risk-engine/settings',
    capabilities: '/risk-engine/capabilities',
    guards: '/risk-engine/guards',
    positionStates: '/risk-engine/positions',
    positionManager: (accountId: string, posId: number) =>
      `/risk-engine/positions/${accountId}/${posId}/manager`,
    styles: '/risk-engine/styles',
    style: (id: string) => `/risk-engine/styles/${id}`,
    conditionalOrders: '/risk-engine/conditional-orders',
    cancelConditionalOrder: (id: string) =>
      `/risk-engine/conditional-orders/${id}`,
    events: '/risk-engine/events',
  },
  ccxt: {
    getExchanges: '/ccxt/exchanges',
    getExchangeCredentials: (exchangeId: string) =>
      `/ccxt/exchanges/${exchangeId}/credentials`,
    validateCredentials: '/ccxt/validate',
  },
  ctrader: {
    getOAuthUrl: '/ctrader/oauth/url',
    oauthCallback: '/ctrader/oauth/callback',
    getAccounts: '/ctrader/accounts',
    addAccount: '/ctrader/accounts/add',
  },
  rithmic: {
    getSystems: '/rithmic/systems',
    discoverAccounts: '/rithmic/accounts/discover',
    addAccounts: '/rithmic/accounts/add',
    frontMonth: '/rithmic/front-month',
    klines: '/rithmic/klines',
  },
  discord: {
    connect: '/discord/auth',
    getProfile: '/discord',
  },
  optionsFlow: {
    gex: (symbol: string) => `/options-flow/gex?symbol=${symbol}`,
    keyLevels: (symbol: string) => `/options-flow/key-levels?symbol=${symbol}`,
    unusualActivity: '/options-flow/unusual-activity',
  },
  positionNotes: {
    list: '/position-notes',
    upsertComment: (posKey: string) => `/position-notes/${posKey}/comment`,
    deleteComment: (posKey: string) => `/position-notes/${posKey}/comment`,
    uploadImage: (posKey: string) => `/position-notes/${posKey}/images`,
    deleteImage: (imageId: string) => `/position-notes/images/${imageId}`,
    imageContent: (imageId: string) =>
      `/position-notes/images/${imageId}/content`,
  },
  tags: {
    list: '/tags',
    create: '/tags',
    update: (id: string) => `/tags/${id}`,
    delete: (id: string) => `/tags/${id}`,
    allPositionTags: '/tags/positions',
    assignTag: (posKey: string) => `/tags/positions/${posKey}`,
    removeTag: (posKey: string, tagId: string) =>
      `/tags/positions/${posKey}/${tagId}`,
  },
  alerts: {
    list: '/alerts',
    create: '/alerts',
    update: (id: string) => `/alerts/${id}`,
    delete: (id: string) => `/alerts/${id}`,
  },
  chartStyles: {
    list: '/chart-styles',
    create: '/chart-styles',
    update: (id: string) => `/chart-styles/${id}`,
    duplicate: (id: string) => `/chart-styles/${id}/duplicate`,
    publish: (id: string) => `/chart-styles/${id}/publish`,
    delete: (id: string) => `/chart-styles/${id}`,
    folders: '/chart-styles/folders',
    createFolder: '/chart-styles/folders',
    updateFolder: (id: string) => `/chart-styles/folders/${id}`,
    deleteFolder: (id: string) => `/chart-styles/folders/${id}`,
    community: (sort: string, limit?: number, offset?: number) =>
      `/chart-styles/community?sort=${sort}${limit != null ? `&limit=${limit}` : ''}${offset != null ? `&offset=${offset}` : ''}`,
    communityById: (id: string) => `/chart-styles/community/${id}`,
    cloneCommunity: (id: string) => `/chart-styles/community/${id}/clone`,
    likeCommunity: (id: string) => `/chart-styles/community/${id}/like`,
    unpublish: (id: string) => `/chart-styles/publications/${id}`,
  },
  communityHub: {
    mine: '/community-hub/mine',
    membership: '/community-hub/membership',
    memberBadge: (userId: string) => `/community-hub/member/${userId}`,
    list: (
      q: {
        status?: string;
        theme?: string;
        platform?: string;
        q?: string;
        sort?: string;
        limit?: number;
        offset?: number;
      } = {},
    ) => {
      const qs = new URLSearchParams();
      if (q.status) qs.set('status', q.status);
      if (q.theme) qs.set('theme', q.theme);
      if (q.platform) qs.set('platform', q.platform);
      if (q.q) qs.set('q', q.q);
      if (q.sort) qs.set('sort', q.sort);
      if (q.limit !== undefined) qs.set('limit', String(q.limit));
      if (q.offset !== undefined) qs.set('offset', String(q.offset));
      const tail = qs.toString();
      return `/community-hub${tail ? `?${tail}` : ''}`;
    },
    create: '/community-hub',
    bySlug: (slug: string) => `/community-hub/${encodeURIComponent(slug)}`,
    update: (id: string) => `/community-hub/${id}`,
    delete: (id: string) => `/community-hub/${id}`,
    pendingLink: (id: string) => `/community-hub/${id}/pending-link`,
    logo: (id: string) => `/community-hub/${id}/logo`,
    banner: (id: string) => `/community-hub/${id}/banner`,
    adminList: (
      q: {
        status?: string;
        linkReview?: string;
        sort?: string;
        limit?: number;
        offset?: number;
      } = {},
    ) => {
      const qs = new URLSearchParams();
      if (q.status) qs.set('status', q.status);
      if (q.linkReview) qs.set('linkReview', q.linkReview);
      if (q.sort) qs.set('sort', q.sort);
      if (q.limit !== undefined) qs.set('limit', String(q.limit));
      if (q.offset !== undefined) qs.set('offset', String(q.offset));
      const tail = qs.toString();
      return `/community-hub/admin${tail ? `?${tail}` : ''}`;
    },
    adminById: (id: string) => `/community-hub/admin/${id}`,
    adminScorePreview: (id: string) =>
      `/community-hub/admin/${id}/score-preview`,
    adminVerify: (id: string) => `/community-hub/admin/${id}/verify`,
    adminReject: (id: string) => `/community-hub/admin/${id}/reject`,
    adminRevoke: (id: string) => `/community-hub/admin/${id}/revoke`,
    adminSuspend: (id: string) => `/community-hub/admin/${id}/suspend`,
    adminUnsuspend: (id: string) => `/community-hub/admin/${id}/unsuspend`,
    adminApproveLink: (id: string) =>
      `/community-hub/admin/${id}/link-review/approve`,
    adminRejectLink: (id: string) =>
      `/community-hub/admin/${id}/link-review/reject`,
    adminOfficial: (id: string) => `/community-hub/admin/${id}/official`,
  },
  marketData: {
    klines: (exchange: string) => `/market-data/${exchange}/klines`,
    ticker: (exchange: string) => `/market-data/${exchange}/ticker`,
    unifiedSymbols: '/market-data/unified-symbols',
    funding: '/market-data/funding',
  },
  algorithms: {
    list: '/algorithms',
    create: '/algorithms',
    get: (id: string) => `/algorithms/${id}`,
    update: (id: string) => `/algorithms/${id}`,
    delete: (id: string) => `/algorithms/${id}`,
    duplicate: (id: string) => `/algorithms/${id}/duplicate`,
    compile: '/algorithms/compile',
    assistant: '/algorithms/assistant',
    createVersion: (id: string) => `/algorithms/${id}/versions`,
    versionCode: (id: string, versionId: string) =>
      `/algorithms/${id}/versions/${versionId}/code`,
    activateVersion: (id: string, versionId: string) =>
      `/algorithms/${id}/versions/${versionId}/activate`,
    images: (id: string) => `/algorithms/${id}/images`,
    deleteImage: (imageId: string) => `/algorithms/images/${imageId}`,
    folders: '/algorithms/folders',
    createFolder: '/algorithms/folders',
    updateFolder: (id: string) => `/algorithms/folders/${id}`,
    deleteFolder: (id: string) => `/algorithms/folders/${id}`,
  },
  backtest: {
    symbols: '/backtest/symbols',
    coverage: (symbol: string) => `/backtest/coverage?symbol=${encodeURIComponent(symbol)}`,
    workerStatus: '/backtest/worker-status',
    runs: (algorithmId?: string, limit?: number) => {
      const qs = new URLSearchParams();
      if (algorithmId) qs.set('algorithmId', algorithmId);
      if (limit != null) qs.set('limit', String(limit));
      const s = qs.toString();
      return `/backtest/runs${s ? `?${s}` : ''}`;
    },
    createRun: '/backtest/runs',
    getRun: (id: string) => `/backtest/runs/${id}`,
    cancelRun: (id: string) => `/backtest/runs/${id}/cancel`,
    deleteRun: (id: string) => `/backtest/runs/${id}`,
    estimate: (kind: string, interval: string, bars: number, runs: number) =>
      `/backtest/estimate?kind=${kind}&interval=${interval}&bars=${bars}&runs=${runs}`,
  },
  algorithmDeployments: {
    list: '/algorithm-deployments',
    create: '/algorithm-deployments',
    update: (id: string) => `/algorithm-deployments/${id}`,
    pause: (id: string) => `/algorithm-deployments/${id}/pause`,
    resume: (id: string) => `/algorithm-deployments/${id}/resume`,
    stop: (id: string) => `/algorithm-deployments/${id}/stop`,
    delete: (id: string) => `/algorithm-deployments/${id}`,
    signals: (id: string) => `/algorithm-deployments/${id}/signals`,
    sends: (id: string) => `/algorithm-deployments/${id}/sends`,
  },
  compute: {
    nodes: '/compute/nodes',
    node: (id: string) => `/compute/nodes/${id}`,
    token: (id: string) => `/compute/nodes/${id}/token`,
  },
  computeCredits: {
    balance: '/compute-credits/balance',
    quote: (amountCents: number) =>
      `/compute-credits/quote?amountCents=${amountCents}`,
    runCost: (kind: string, interval: string, bars: number, runs: number) =>
      `/compute-credits/run-cost?kind=${kind}&interval=${interval}&bars=${bars}&runs=${runs}`,
    purchase: '/compute-credits/purchase',
    confirm: (invoiceId: string) =>
      `/compute-credits/purchase/${encodeURIComponent(invoiceId)}/confirm`,
    ledger: (limit: number) => `/compute-credits/ledger?limit=${limit}`,
  },
} as const;

const API_URL = import.meta.env.VITE_API_URL;

const client = axios.create({
  baseURL: API_URL,
});

/** Verbes sans effet de bord, plus les deux POST de calcul de cache. */
const DEMO_SAFE_METHODS = new Set(['get', 'head', 'options']);
const DEMO_SAFE_POSTS = [
  '/tracked-account/stats/simulate/',
  '/footprint-cache/compute',
  '/excursions/compute',
];

client.interceptors.request.use(async (config) => {
  // En démonstration, l'écriture est coupée ici et pas seulement au serveur :
  // aucune requête ne part, même si un contrôle grisé était réactivé depuis la
  // console. Le garde serveur reste l'autorité, ceci en est le doublon local.
  // Lecture directe du store plutôt que `isDemoMode()` : demo-mode.ts importe
  // ce client, l'importer en retour créerait un cycle.
  const method = (config.method ?? 'get').toLowerCase();
  const url = config.url ?? '';
  const blocked =
    window.store.get(DEMO_MODE) === 'true' &&
    !DEMO_SAFE_METHODS.has(method) &&
    !(
      method === 'post' && DEMO_SAFE_POSTS.some((safe) => url.includes(safe))
    ) &&
    url !== routes.auth.demo &&
    url !== routes.auth.token;
  if (blocked) {
    window.dispatchEvent(new CustomEvent(DEMO_BLOCKED_EVENT));
    throw new axios.Cancel('DEMO_READ_ONLY');
  }

  // La paire renouvelée est rangée par getAccessToken, sous son verrou : la
  // ranger ici, une microtâche plus tard, laissait la requête suivante rejouer
  // un jeton déjà consommé (voir utils/auth.ts).
  // Un envoi qui survit à la page (keepalive, à la fermeture) ne lance jamais
  // d'échange, ni ici ni sur un 401 : la réponse se perdrait avec la page, et
  // le jeton consommé resté sur le poste vaudrait « vol » au lancement
  // suivant, qui déconnecterait. Il part avec le jeton rangé ; expiré, il est
  // refusé et repart au lot suivant si la page vit encore.
  const token = config.fetchOptions?.keepalive
    ? window.store.get(ACCESS_TOKEN)
    : (await getAccessToken())?.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // L'admin qui regarde l'application à la place d'un utilisateur parle en son
  // nom, sauf pour une requête qui le refuse (`OverrideUserId: false`) : elle
  // part alors au nom de l'admin lui-même, comme l'exige une route de son rôle.
  const overrideUserId = window.store.get('admin_override_user_id');
  if (overrideUserId && config.headers.OverrideUserId !== false) {
    config.headers.OverrideUserId = overrideUserId;
  }
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

client.interceptors.response.use(undefined, async (error) => {
  if (isAxiosError(error) && error.response?.status === 401) {
    // Sans session ouverte, l'erreur remonte telle quelle (écran de connexion).
    if (!window.store.get(REFRESH_TOKEN)) throw error;
    // Un 401 ne déconnecte plus d'emblée : le jeton refusé est renouvelé et
    // la requête rejouée une fois. Seul un second refus, ou un
    // renouvellement que le serveur refuse, ferme la session.
    const config = error.config as RetriableConfig | undefined;
    // Envoi de fermeture : pas d'échange (voir l'intercepteur de requête).
    if (config?.fetchOptions?.keepalive) throw error;
    if (!config || config._retried) {
      AuthService.signout();
      throw error;
    }
    config._retried = true;
    const rejected = String(config.headers?.Authorization ?? '').replace(
      /^Bearer /,
      '',
    );
    let token: Awaited<ReturnType<typeof getAccessToken>>;
    try {
      token = await getAccessToken({ rejected });
    } catch {
      // Serveur injoignable : la requête échoue, la session reste.
      throw error;
    }
    if (!token) {
      AuthService.signout();
      throw error;
    }
    return client.request(config);
  } else if (
    isAxiosError(error) &&
    error.response?.status === 403 &&
    (error.response.data as { message?: string })?.message === 'DEMO_READ_ONLY'
  ) {
    // Écriture refusée sur le compte de démonstration : on prévient
    // l'utilisateur plutôt que de laisser remonter une erreur brute.
    window.dispatchEvent(new CustomEvent(DEMO_BLOCKED_EVENT));
    throw error;
  } else {
    throw error;
  }
});

export default client;
